import { useEffect, useState, useRef } from 'react';
import { FileCode, Trash2, RotateCcw } from 'lucide-react';
import DynamicProgressModal from '../DynamicProgressModal/view';
import XmlUploader from '../../molecules/XmlUploader/view';
import './style.css';
import ModalBtn from '../../molecules/modalButton/view';
import ArrayRelater from '../../molecules/ArrayRelater/view';
import ProductSearchDropdown from '../../molecules/ArrayRelater/ProductSearchDropdown';
import EditProduct from '../editProduct/view';
import ButtonAction from '../../atoms/buttonAction/view';
import ThirdsTable from '../thirdsTable/view';
import PaymentMethodSelector from '../../molecules/PaymentMethodSelector/view';
import XmlPreview from '../PipelineDraftsManager/molecules/XmlPreview/view';
import ClientInfo from '../../atoms/ClientInfo/view';
import DateSelector from '../../atoms/datepicker/view';
import TaxSearchDropdown from '../../molecules/TaxSearchDropdown/view';
import { Form } from 'react-bootstrap';

import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { createBuy } from '../../../usecases/buys-usecase';
import { getTaxes } from '../../../usecases/taxes-usecase';
import { formatMoney } from '../../../utils/formating';
import { CheckCircle, RadioButtonUnchecked, FiberManualRecord } from '@mui/icons-material';

function getPosTaxRate(product, mapInput = {}) {
  if (!product) return null;
  const map = (mapInput && mapInput.current) ? mapInput.current : (mapInput || {});

  const taxId = product.buy_taxes_id ?? product.buy_tax_id ?? product.tax_id;
  if (taxId != null && map[taxId] != null) {
    return map[taxId];
  }

  const buyTaxesObj = Array.isArray(product.buy_taxes) ? product.buy_taxes[0] : product.buy_taxes;
  if (buyTaxesObj) {
    let r = parseFloat(buyTaxesObj.value ?? buyTaxesObj.rate ?? 0);
    if (!isNaN(r)) return r > 0 && r < 1 ? r * 100 : r;
  }

  if (product.tax_rate != null && !isNaN(parseFloat(product.tax_rate))) {
    let r = parseFloat(product.tax_rate);
    return r > 0 && r < 1 ? r * 100 : r;
  }
  if (product.tax != null && !isNaN(parseFloat(product.tax))) {
    let r = parseFloat(product.tax);
    return r > 0 && r < 1 ? r * 100 : r;
  }

  return null;
}

const BuyFromXml = ({ profile, hidden = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [invoiceData, setInvoiceData] = useState(null);
  const [matchedItems, setMatchedItems] = useState([]);
  const [selectedThird, setSelectedThird] = useState(null);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buyDate, setBuyDate] = useState(new Date());
  const [buyDocument, setBuyDocument] = useState("");
  const [selectedRetention, setSelectedRetention] = useState(undefined);
  const [retentionValue, setRetentionValue] = useState(0);
  const [taxesMap, setTaxesMap] = useState({});
  const taxesMapRef = useRef({});
  taxesMapRef.current = taxesMap;

  useEffect(() => {
    getTaxes(1, 1000)
      .then(res => {
        const list = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);
        const map = {};
        list.forEach(t => {
          if (t && t.id != null) {
            const val = parseFloat(t.value || t.rate || 0);
            map[t.id] = val < 1 && val > 0 ? val * 100 : val;
          }
        });
        setTaxesMap(map);
      })
      .catch(err => console.error("[BuyFromXml] Error fetching taxes map:", err));
  }, []);

  useEffect(() => {
    if (Object.keys(taxesMap).length > 0 && matchedItems.length > 0) {
      setMatchedItems(prev => prev.map(item => {
        const resolvedPosRate = getPosTaxRate(item, taxesMap);
        if (resolvedPosRate == null) return item;

        const xmlRate = parseFloat(item.xmlTaxRate ?? item.taxRate ?? 0);
        const effectiveTaxRate = resolvedPosRate;
        const subtotal = parseFloat(item.subtotal || (item.amount * item.buy_value) || 0);
        const auxtax = (subtotal * effectiveTaxRate) / 100;
        const total = subtotal + auxtax;
        const hasTaxMismatch = Math.abs(xmlRate - effectiveTaxRate) > 0.01;

        return {
          ...item,
          taxRate: effectiveTaxRate,
          posTaxRate: resolvedPosRate,
          hasTaxMismatch: hasTaxMismatch,
          auxtax: auxtax,
          total: total
        };
      }));
    }
  }, [taxesMap]);

  useEffect(() => {
    // Escuchar cuando se selecciona un tercero en la tabla y avanzar al paso de Pago
    const cleanThird = listener("btn-thirds-add", (e) => {
      if (e.detail?.data) {
        setSelectedThird(e.detail.data);
        setCurrentStep(3); // Avanzar a Paso 4 (Pago)
      }
    });
    const cleanOpenWithData = listener("open-buy-from-xml-with-data", (e) => {
      const data = e.detail?.data;
      if (data) {
        setInvoiceData(data);

        if (data.prefilled) {
          // Viene del Auxiliar Contable con datos ya procesados → precargar e iniciar en el primer paso
          if (data.matchedItems?.length) {
            setMatchedItems(data.matchedItems);
          }
          if (data.selectedMethods?.length) {
            setSelectedMethods(data.selectedMethods);
          }
          if (data.selectedFormPay) {
            setSelectedFormPay(data.selectedFormPay);
          }
          if (data.tercero) {
            setSelectedThird(data.tercero);
          }
          setCurrentStep(0); // Iniciar en el Paso 1 (Carga XML pre-cargado)
        } else {
          setCurrentStep(1); // Avanzar al paso de relacionar productos
        }

        // Abrir el modal programáticamente
        dispatchCustomEvent('modalbuyfromxml-btn', { eventKey: 'buyfromxml-btn' });
      }
    });
    const cleanProductEdited = listener('product-edited', (e) => {
      const rawData = e?.detail?.data;
      const updatedProduct = rawData?.detail?.data || rawData?.data || rawData;
      if (!updatedProduct) return;

      const prodId = updatedProduct.id || updatedProduct.product_id;
      const prodSku = updatedProduct.sku || updatedProduct.originalSku;

      setMatchedItems(prev => prev.map((item, index) => {
        const isMatch =
          (updatedProduct.rowIdx !== undefined && updatedProduct.rowIdx === index) ||
          (prodId && item.id == prodId) ||
          (prodSku && item.sku && String(item.sku) === String(prodSku));

        if (isMatch) {
          const merged = { ...item, ...updatedProduct };
          const buyValue = parseFloat(merged.buy_value ?? item.buy_value ?? 0);
          const resolvedPosRate = getPosTaxRate(merged, taxesMapRef);
          const xmlRate = parseFloat(item.xmlTaxRate ?? item.taxRate ?? 0);

          const effectiveTaxRate = resolvedPosRate != null ? resolvedPosRate : xmlRate;
          const hasTaxMismatch = Math.abs(xmlRate - effectiveTaxRate) > 0.01;

          const amount = parseFloat(item.amount || 1);
          const subtotal = amount * buyValue;
          const tax = (subtotal * effectiveTaxRate) / 100;
          const total = subtotal + tax;

          return {
            ...merged,
            matchedName: merged.name || item.matchedName || item.name,
            buy_value: buyValue,
            taxRate: effectiveTaxRate,
            posTaxRate: effectiveTaxRate,
            xmlTaxRate: xmlRate,
            hasTaxMismatch: hasTaxMismatch,
            subtotal: subtotal,
            auxtax: tax,
            total: total
          };
        }
        return item;
      }));
    });

    return () => {
      cleanThird();
      cleanOpenWithData();
      if (cleanProductEdited) cleanProductEdited();
    };
  }, []);

  const activeItems = matchedItems.filter(item => !item.disabled);

  const totalBase = activeItems.reduce((acc, item) => acc + (parseFloat(item.subtotal) || 0), 0);
  const totalTaxes = activeItems.reduce((acc, item) => acc + (parseFloat(item.auxtax) || 0), 0);
  const totalAmount = totalBase + totalTaxes;

  const taxesByRate = activeItems.reduce((acc, item) => {
    const taxVal = parseFloat(item.auxtax) || 0;
    if (taxVal <= 0) return acc;

    let rate = parseFloat(item.taxRate || 0);
    if ((isNaN(rate) || rate === 0) && item.subtotal > 0) {
      rate = Math.round((taxVal / item.subtotal) * 100);
    }

    const key = rate > 0 ? `IVA (${rate}%)` : 'Impuestos / Otros';
    acc[key] = (acc[key] || 0) + taxVal;
    return acc;
  }, {});

  if (Object.keys(taxesByRate).length === 0 && (parseFloat(invoiceData?.tax) || 0) > 0) {
    taxesByRate['IVA / Impuestos'] = parseFloat(invoiceData.tax);
  }

  useEffect(() => {
    if (invoiceData) {
      if (invoiceData.id) setBuyDocument(invoiceData.id);
      if (invoiceData.issueDate) {
        const dateStr = String(invoiceData.issueDate).substring(0, 10);
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          setBuyDate(new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0));
        } else {
          setBuyDate(new Date(invoiceData.issueDate));
        }
      }

      if (invoiceData.paymentMeansCode && !selectedFormPay) {
        const code = invoiceData.paymentMeansCode;
        if (code === "1" || code === "10") {
            setSelectedFormPay("1");
        } else if (code === "2" || code === "ZZZ" || invoiceData.paymentDueDate) {
            setSelectedFormPay("2");
        }
      } else if (invoiceData.paymentDueDate && !selectedFormPay) {
        setSelectedFormPay("2");
      }
    }
  }, [invoiceData]);

  useEffect(() => {
    if (selectedRetention && !isNaN(parseFloat(selectedRetention["value"]))) {
      const retention = parseFloat(selectedRetention["value"]);
      const calculatedRetention = totalBase * retention;
      setRetentionValue(Math.round(calculatedRetention));
    } else {
      setRetentionValue(0);
    }
  }, [totalBase, selectedRetention]);

  function handleRetention(retention_selected) {
    if (!retention_selected || isNaN(parseFloat(retention_selected["value"]))) {
      setSelectedRetention(undefined);
      setRetentionValue(0);
      return;
    }
    setSelectedRetention(retention_selected);
  }

  async function handleFinish() {
    if (!selectedThird) {
      dispatchCustomEvent("popup", { message: "Debe seleccionar un proveedor.", type: "warning" });
      return;
    }
    if (selectedMethods.length === 0) {
      dispatchCustomEvent("popup", { message: "Debe agregar al menos un medio de pago.", type: "warning" });
      return;
    }

    const payload = {
      tercero: selectedThird,
      expiration: 0,
            documento_compra: buyDocument || invoiceData?.id || '',
      buy_date: buyDate || invoiceData?.issueDate || new Date().toISOString(),
      methods: selectedMethods.map(method => ({
        ...method,
        payment_form_id: parseInt(selectedFormPay) || 1
      })),
      total: totalAmount,
      retention: selectedRetention || null,
      retention_value: retentionValue || 0,
      items: activeItems,
      taxes: [],
      xml_invoice_data: invoiceData ? {
        cufe: invoiceData.cufe || null,
        invoice_number: invoiceData.id || null,
        supplier_nit: invoiceData.supplierNIT || null,
        supplier_name: invoiceData.supplier || null,
        supplier_email: invoiceData.supplierEmail || null,
        payment_due_date: invoiceData.paymentDueDate || null,
        payment_means_code: invoiceData.paymentMeansCode || null,
      } : null
    };

    setIsSubmitting(true);
    try {
      await createBuy(payload);
      if (invoiceData?.draft_id) {
        dispatchCustomEvent("discard-pipeline-draft", { id: invoiceData.draft_id });
      }
      dispatchCustomEvent("popup", { message: "Compra guardada con éxito desde XML", type: "success" });
      dispatchCustomEvent("reload-list-compras", {});
      dispatchCustomEvent("reload-pipeline-drafts", {});
      dispatchCustomEvent("update-stock", {});
      dispatchCustomEvent("modal-close", null);
      setTimeout(() => {
        setCurrentStep(0);
                setInvoiceData(null);
        setSelectedThird(null);
        setMatchedItems([]);
        setSelectedMethods([]);
        setSelectedFormPay('1');
        setIsSubmitting(false);
        setBuyDocument("");
        setBuyDate(new Date());
        setSelectedRetention(undefined);
        setRetentionValue(0);
      }, 300);
    } catch (error) {
      console.error("[BuyFromXml] Error creating buy:", error);
      const msg = error?.response?.data?.message || error?.message || "Error al crear la compra";
      dispatchCustomEvent("popup", { message: msg, type: "error" });
      setIsSubmitting(false);
    }
  }

  const handleProductsMatch = (items) => {
    const formatted = items.map(item => {
      const product = { ...item.relatedItem };
      const xmlTax = parseFloat(item.tax || 0);
      const amount = parseFloat(item.quantity || 1);
      const buyValue = parseFloat(item.unit_price || item.price || 0);
      const subtotal = parseFloat(item.subtotal || (amount * buyValue) || 0);

      // XML Tax Rate
      let xmlTaxRate = parseFloat(item.taxRate ?? item.tax_rate ?? 0);
      if ((!xmlTaxRate || isNaN(xmlTaxRate)) && xmlTax > 0 && subtotal > 0) {
        xmlTaxRate = Math.round((xmlTax / subtotal) * 100);
      }

      // POS Product Tax Rate (resolved via getPosTaxRate using taxesMapRef)
      const resolvedPosRate = getPosTaxRate(product, taxesMapRef);
      const hasPosProduct = !!(product.id || item.product_id);

      const effectiveTaxRate = (hasPosProduct && resolvedPosRate != null) ? resolvedPosRate : xmlTaxRate;

      const auxtax = (subtotal * effectiveTaxRate) / 100;
      const total = subtotal + auxtax;
      const hasTaxMismatch = hasPosProduct && resolvedPosRate != null && Math.abs(xmlTaxRate - resolvedPosRate) > 0.01;

      return {
        ...product,
        id: product.id || item.product_id,
        xmlName: item.description || item.name || 'Producto XML',
        matchedName: product.name || 'Sin vincular',
        buy_value: buyValue,
        auxsku: item.idx,
        amount: amount,
        subtotal: subtotal,
        auxtax: auxtax,
        taxRate: effectiveTaxRate,
        xmlTaxRate: xmlTaxRate,
        posTaxRate: resolvedPosRate ?? xmlTaxRate,
        hasTaxMismatch: hasTaxMismatch,
        total: total
      };
    });
    setMatchedItems(formatted);
    setCurrentStep(2); // Avanzar a Paso 3 (Confirmar Proveedor)
  };

  const handleToggleItem = (index) => {
    setMatchedItems(prev => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        disabled: !copy[index].disabled
      };
      return copy;
    });
  };

  const handleRemoveItem = (index) => {
    handleToggleItem(index);
  };

  const handleUpdateItem = (index, field, value) => {
    const numVal = Math.max(0, parseFloat(value) || 0);
    setMatchedItems(prev => {
      const copy = [...prev];
      const item = { ...copy[index] };

      if (field === 'amount') {
        item.amount = numVal;
      } else if (field === 'buy_value') {
        item.buy_value = numVal;
      }

      const rate = parseFloat(item.taxRate || 0);
      const subtotal = item.amount * item.buy_value;
      const tax = (subtotal * rate) / 100;
      const total = subtotal + tax;

      item.subtotal = subtotal;
      item.auxtax = tax;
      item.total = total;

      copy[index] = item;
      return copy;
    });
  };

  const handleAddProduct = (selectedProduct) => {
    if (!selectedProduct) return;
    let rate = parseFloat(selectedProduct.tax_rate ?? selectedProduct.tax ?? selectedProduct.buy_taxes?.value ?? 0);
    if (rate > 0 && rate < 1) rate = rate * 100;

    const buyValue = parseFloat(selectedProduct.buy_value ?? selectedProduct.price ?? 0);
    const amount = 1;
    const subtotal = amount * buyValue;
    const tax = (subtotal * rate) / 100;
    const total = subtotal + tax;

    const newItem = {
      ...selectedProduct,
      id: selectedProduct.id,
      xmlName: selectedProduct.name,
      matchedName: selectedProduct.name,
      buy_value: buyValue,
      auxsku: selectedProduct.id,
      amount: amount,
      subtotal: subtotal,
      auxtax: tax,
      taxRate: rate,
      xmlTaxRate: rate,
      posTaxRate: rate,
      hasTaxMismatch: false,
      total: total,
    };

    setMatchedItems(prev => [...prev, newItem]);
  };

  const steps = [
    {
      title: 'Cargar factura XML',
      component: () => (
        <div style={{ maxHeight: '60vh', width: '100%', overflowY: "auto" }}>
          <XmlUploader handleData={setInvoiceData} invoiceData={invoiceData} />
        </div>
      ),
      showContinueButton: !!invoiceData,
    },
    {
      title: 'Relacionar Productos',
      component: () => (
        <div style={{ maxHeight: '72vh', width: '100%', overflow: 'hidden', paddingBottom: '10px' }}>
          <ArrayRelater
            profile={profile}
            array1={invoiceData?.items || []}
            handleData={handleProductsMatch}
          />
        </div>
      ),
      showContinueButton: false,
    },
    {
      title: 'Confirmar Proveedor',
      component: () => (
        <div className="thirds-step-container">
          {invoiceData && (
            <div
              className="xml-supplier-info-card"
              style={{
                backgroundColor: 'var(--color-surface, #1e293b)',
                border: '1px solid var(--color-border, #334155)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: '11px',
                    color: 'var(--color-text-muted, #94a3b8)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                  }}
                >
                  Proveedor extraído de la factura XML:
                </span>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'var(--color-text, #f8fafc)',
                    marginTop: '2px',
                  }}
                >
                  {invoiceData.supplier || 'Proveedor sin nombre'}{' '}
                  <span style={{ fontWeight: '400', opacity: 0.85 }}>
                    ({invoiceData.supplierNIT ? `NIT: ${invoiceData.supplierNIT}` : 'Sin NIT'})
                  </span>
                </div>
              </div>
            </div>
          )}
          <ThirdsTable
            id="buyfromxml-thirdstable"
            mode="selector"
            forceLoadData={true}
            profile={profile}
            maxHeight="300px"
            filter={invoiceData?.supplierNIT}
            initialThird={
              invoiceData?.supplierNIT ? {
                identification: invoiceData.supplierNIT,
                name: invoiceData.supplier,
                email: invoiceData.supplierEmail,
                phone: invoiceData.supplierPhone
              } : {}
            }
          />
        </div>
      ),
      showContinueButton: !!selectedThird,
    },
    {
      title: 'Pago y Confirmación',
      component: () => (
        <div style={{ height: 'calc(68vh - 40px)', width: '100%', overflow: 'hidden', paddingBottom: '10px' }}>
          <div className="row h-100 g-3">
            {/* Panel Izquierdo (60%): Resumen XML y Detalle Comparativo de Productos */}
            <div className="col-12 col-lg-7 h-100 d-flex flex-column" style={{ borderRight: '1px solid var(--color-border, #334155)', paddingRight: '15px', overflow: 'hidden' }}>
              {invoiceData?.rawContent ? (
                <div style={{ maxHeight: '180px', flexShrink: 0, overflowY: 'auto', marginBottom: '10px' }}>
                  <XmlPreview base64Data={btoa(unescape(encodeURIComponent(invoiceData.rawContent)))} />
                </div>
              ) : (
                <div className="p-3 rounded mb-2 flex-shrink-0" style={{ background: 'var(--color-surface, #1e293b)', border: '1px solid var(--color-border, #334155)' }}>
                  <h6 className="fw-bold mb-2 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>📄 DATOS EXTRAÍDOS DEL XML</h6>
                  <div className="d-flex flex-column gap-2" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Documento:</span>
                      <strong className="text-light">{invoiceData?.id || 'N/A'}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Fecha Emisión:</span>
                      <strong className="text-light">{invoiceData?.issueDate ? String(invoiceData.issueDate).substring(0, 10) : 'N/A'}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">CUFE:</span>
                      <strong className="text-truncate text-light" style={{ maxWidth: '200px' }} title={invoiceData?.cufe}>{invoiceData?.cufe || 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              )}
              {/* Resumen Comparativo de Productos (XML vs Seleccionado) - Carrito Interactivo */}
              <div className="p-3 rounded d-flex flex-column flex-grow-1" style={{ background: 'var(--color-surface, #1e293b)', border: '1px solid var(--color-border, #334155)', minHeight: 0, overflow: 'hidden' }}>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-shrink-0">
                  <h6 className="fw-bold m-0 text-success" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>📦 PRODUCTOS ({matchedItems.length})</h6>
                  <div style={{ width: '220px' }}>
                    <ProductSearchDropdown
                      profile={profile}
                      onSelected={(prod) => handleAddProduct(prod)}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '14px' }}>
                  {matchedItems.length === 0 ? (
                    <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>
                      No hay productos en el carrito. Agrega uno usando el buscador.
                    </div>
                  ) : (
                    matchedItems.map((item, idx) => {
                      const isDisabled = !!item.disabled;
                      return (
                        <div
                          key={idx}
                          className="border-bottom py-2"
                          style={{
                            fontSize: '12px',
                            borderColor: 'var(--color-border, #334155)',
                            opacity: isDisabled ? 0.45 : 1,
                            backgroundColor: isDisabled ? 'rgba(51, 65, 85, 0.25)' : 'transparent',
                            filter: isDisabled ? 'grayscale(80%)' : 'none',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            marginBottom: '4px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {item.xmlName && (
                            <div className="text-muted text-truncate mb-1" style={{ fontSize: '11px', fontWeight: 500, textDecoration: isDisabled ? 'line-through' : 'none' }}>
                              📄 XML: <span className="text-light">{item.xmlName}</span>
                              {isDisabled && <span className="badge bg-secondary ms-2" style={{ fontSize: '9px', textDecoration: 'none' }}>DESHABILITADO</span>}
                            </div>
                          )}
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong className="text-info text-truncate" style={{ maxWidth: '75%', textDecoration: isDisabled ? 'line-through' : 'none' }}>
                              ➜ POS: {item.matchedName || item.name || item.xmlName}
                            </strong>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                              {!isDisabled && item.id && (
                                <EditProduct
                                  data={{ ...item, rowIdx: idx }}
                                  modalId={`edit-product-row-${idx}`}
                                  profile={profile}
                                  hidden={false}
                                />
                              )}
                              <ButtonAction
                                variant={isDisabled ? "primary" : "danger"}
                                title={isDisabled ? <RotateCcw size={16} /> : <Trash2 size={16} />}
                                onClick={() => handleToggleItem(idx)}
                              />
                            </div>
                          </div>

                          <div className="d-flex align-items-center justify-content-between mt-1" style={{ fontSize: '11px', flexWrap: 'nowrap', gap: '6px' }}>
                            <div className="d-flex align-items-center gap-1">
                              <span className="text-muted">Cant:</span>
                              <input
                                type="number"
                                min="0.01"
                                step="any"
                                disabled={isDisabled}
                                value={item.amount}
                                onChange={(e) => handleUpdateItem(idx, 'amount', e.target.value)}
                                className="form-control form-control-sm text-center px-1 py-0"
                                style={{ width: '60px', height: '24px', backgroundColor: 'var(--color-surface, #0f172a)', color: '#f8fafc', borderColor: '#334155', fontSize: '11px' }}
                              />
                            </div>

                            <div className="d-flex align-items-center gap-1">
                              <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>Vr. Unit.:</span>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                disabled={isDisabled}
                                value={item.buy_value}
                                onChange={(e) => handleUpdateItem(idx, 'buy_value', e.target.value)}
                                className="form-control form-control-sm text-end px-1 py-0"
                                style={{ width: '85px', height: '24px', backgroundColor: 'var(--color-surface, #0f172a)', color: '#f8fafc', borderColor: '#334155', fontSize: '11px' }}
                              />
                            </div>

                            <div className="text-end">
                              <strong className="text-light" style={{ textDecoration: isDisabled ? 'line-through' : 'none' }}>
                                {formatMoney(item.total)}
                              </strong>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mt-1 text-muted" style={{ fontSize: '10px' }}>
                            <span>Base: {formatMoney(item.subtotal || (item.total - (item.auxtax || 0)))}</span>
                            <div className="d-flex align-items-center gap-1">
                              <span className={!isDisabled && item.auxtax > 0 ? "text-warning fw-semibold" : "text-muted"}>
                                Imp. {item.taxRate > 0 ? `(${item.taxRate}%)` : '(0%)'}: +{formatMoney(item.auxtax || 0)}
                              </span>
                              {!isDisabled && item.hasTaxMismatch && (
                                <span
                                  className="badge bg-warning text-dark"
                                  style={{ fontSize: '9px', padding: '2px 5px', borderRadius: '4px', cursor: 'help' }}
                                  title={`Atención: El impuesto de este producto en el POS es ${item.taxRate}% pero en el XML venía como ${item.xmlTaxRate}%.`}
                                >
                                  ⚠️ XML {item.xmlTaxRate}% ≠ POS {item.taxRate}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Panel Derecho (40%): Proveedor Estándar y Selección de Método de Pago */}
            <div className="col-12 col-lg-5 h-100 d-flex flex-column" style={{ overflowY: 'auto', paddingLeft: '15px', paddingBottom: '15px' }}>
                            <div className="mb-3">
                <span className="text-muted d-block mb-2" style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 600 }}>PROVEEDOR CONFIRMADO</span>
                <ClientInfo
                  tercero={selectedThird || { name: invoiceData?.supplier, identification: invoiceData?.supplierNIT }}
                  type="provider"
                  notActions={true}
                />
              </div>

              {/* Resumen de Facturación: Subtotal Base, Impuestos, Total XML, Retención */}
              <div className="mb-3 p-3 rounded" style={{ background: 'var(--color-surface, #1e293b)', border: '1px solid var(--color-border, #334155)' }}>
                <span className="text-muted d-block mb-2" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>RESUMEN DE FACTURACIÓN</span>
                
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: '13px' }}>
                  <span className="text-muted">Subtotal Base:</span>
                  <strong className="text-light">{formatMoney(totalBase)}</strong>
                </div>

                {Object.entries(taxesByRate).length > 0 ? (
                  Object.entries(taxesByRate).map(([label, val]) => (
                    <div key={label} className="d-flex justify-content-between mb-1" style={{ fontSize: '13px' }}>
                      <span className="text-muted">{label}:</span>
                      <strong className="text-info">+{formatMoney(val)}</strong>
                    </div>
                  ))
                ) : (
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: '13px' }}>
                    <span className="text-muted">Impuestos (IVA):</span>
                    <strong className="text-info">+{formatMoney(totalTaxes)}</strong>
                  </div>
                )}

                <div className="d-flex justify-content-between pt-2 mt-2 border-top" style={{ borderColor: 'var(--color-border, #334155)', fontSize: '14px' }}>
                  <span className="text-light fw-bold">Total Factura XML:</span>
                  <strong className="text-light fw-bold" style={{ fontSize: '16px' }}>{formatMoney(totalAmount)}</strong>
                </div>

                {retentionValue > 0 && (
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: '13px' }}>
                    <span className="text-danger">Retención ({selectedRetention?.name || selectedRetention?.label}):</span>
                    <strong className="text-danger">-{formatMoney(retentionValue)}</strong>
                  </div>
                )}

                {retentionValue > 0 && (
                  <div className="d-flex justify-content-between pt-2 mt-1 border-top" style={{ borderColor: 'var(--color-border, #334155)', fontSize: '15px' }}>
                    <span className="text-success fw-bold">Total a Pagar:</span>
                    <strong className="text-success fw-bold" style={{ fontSize: '17px' }}>{formatMoney(totalAmount - retentionValue)}</strong>
                  </div>
                )}
              </div>

              <div className="mb-3" style={{ background: 'var(--color-surface, #1e293b)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border, #334155)' }}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted" style={{ fontSize: '12px', fontWeight: 600 }}>FECHA DE OPERACIÓN</Form.Label>
                  <DateSelector
                      label="Seleccionar fecha"
                      date={buyDate}
                      onChange={(date) => setBuyDate(date)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="text-muted" style={{ fontSize: '12px', fontWeight: 600 }}>RETENCIÓN</Form.Label>
                  <TaxSearchDropdown
                      selected={selectedRetention}
                      onSelected={(retention) => handleRetention(retention)}
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label className="text-muted" style={{ fontSize: '12px', fontWeight: 600 }}>DOCUMENTO DE COMPRA</Form.Label>
                  <Form.Control
                      id="document-buy"
                      placeholder="Nro Factura / Documento"
                      value={buyDocument}
                      onChange={(e) => setBuyDocument(e.target.value)}
                      style={{ backgroundColor: 'var(--color-surface, #1e293b)', color: 'var(--color-text, #f8fafc)', borderColor: 'var(--color-border, #334155)' }}
                  />
                </Form.Group>
              </div>

              <div style={{ flex: 1 }}>
                <PaymentMethodSelector
                  id="buyfromxml-payment-methods"
                  selectedMethods={selectedMethods}
                  onSelectMethod={setSelectedMethods}
                  onAmountChange={setSelectedMethods}
                  onRemoveMethod={setSelectedMethods}
                  enableFormPay={true}
                  selectedFormPay={setSelectedFormPay}
                  selectedFormPayValue={selectedFormPay}
                  defaultAmount={totalAmount - retentionValue}
                  suggestedValue={totalAmount - retentionValue}
                />
              </div>

              <div className="d-flex justify-content-end mt-3 pt-2 border-top mb-2" style={{ borderColor: 'var(--color-border, #334155)' }}>
                <button
                  onClick={handleFinish}
                  disabled={selectedMethods.length === 0 || isSubmitting}
                  className="btn btn-primary px-4 py-2"
                  style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-primary, #0d6efd)',
                    borderColor: 'var(--color-primary, #0d6efd)',
                    minWidth: '200px'
                  }}
                >
                  {isSubmitting ? 'Guardando...' : 'Finalizar Compra'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      showContinueButton: false,
    }
  ];

  const CustomStepper = () => (
    <div className="custom-stepper">
      {['Carga', 'Vínculo', 'Proveedor', 'Pago'].map((label, idx) => (
        <div key={label} className={`step-item ${currentStep === idx ? 'active' : ''} ${currentStep > idx ? 'completed' : ''}`} onClick={() => setCurrentStep(idx)} style={{ cursor: 'pointer' }}>
          <div className="step-icon">
            {currentStep > idx ? <CheckCircle /> : currentStep === idx ? <FiberManualRecord /> : <RadioButtonUnchecked />}
          </div>
          <span className="step-label">{label}</span>
          {idx < 3 && <div className="step-line" />}
        </div>
      ))}
    </div>
  );

  return (
    <ModalBtn id="buyfromxml-btn" title="Cargar desde XML" icon={<FileCode size={20} />} hidden={hidden} style={{ width: '90vw', height: '90vh' }}>
      <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <CustomStepper />
        <DynamicProgressModal
          currentStepIndex={currentStep}
          onStepChange={setCurrentStep}
          steps={steps}
          onFinish={handleFinish}
          hiddenBar={true}
          hiddenTitle={true}
        />
      </div>
    </ModalBtn>
  );
};

export default BuyFromXml;
